import { Link } from '@achieve/ascend'
import { Typography } from 'components/Typography'
import { actualDevice, DEVICES, withUuid } from 'utils/shared'
import { memo, useEffect, useState } from 'react'
import PersonalLogo from 'components/Svgs/PersonalLogo'
import DebtResolutionLogo from 'components/Svgs/DebtResolutionLogo'
import GoodLogo from 'components/Svgs/GoodLogo'
import MoloLogo from 'components/Svgs/MoloLogo'
import HomeLogo from 'components/Svgs/HomeLogo'
import { DTI_RATIO_ASSESSMENT_MAP, GOOD_APP_LINKS, MOLO_APP_LINKS } from '../tools'
import { StoreBadge, STORE_KEYS } from './StoreBadge'

// Phones only get their own store; desktop (or an unknown device) gets both
const STORES_BY_DEVICE = {
  [DEVICES.ios]: ['appStore'],
  [DEVICES.android]: ['googlePlay'],
}

// MoLO and GOOD link straight to the stores; OneLink was decommissioned with AppsFlyer
const AppRecommendationCard = ({ card }) => {
  const Logo = card.icon
  return (
    <div className="px-4 flex flex-col items-center text-center">
      <Logo />
      <Typography variant="bodyXs" className="text-[#1d252f] mt-1 mb-2 whitespace-nowrap">
        {card.text}
      </Typography>
      <div className="flex flex-col gap-2">
        {(STORES_BY_DEVICE[card.device] || STORE_KEYS)
          .filter((store) => card.stores?.[store])
          .map((store) => (
            <StoreBadge
              key={store}
              store={store}
              href={card.stores[store]}
              onClick={() => card.onClick?.(store)}
            />
          ))}
      </div>
    </div>
  )
}

const RecommendationCard = memo(({ card, isLast }) => {
  const Logo = card.icon
  return (
    <div
      className={`flex flex-row justify-center ${
        !isLast &&
        "before:content-[''] before:absolute before:right-0 before:top-1/4 before:bottom-1/4 before:w-px before:bg-[#C0CBD8]"
      }`}
    >
      {card.stores ? (
        <AppRecommendationCard card={card} />
      ) : (
        <Link
          href={card.link}
          className="p-2 px-4 rounded flex flex-col items-center justify-center text-center h-[75px] w-[109px] recommendation-card no-underline hover:bg-[#EFF5FF]"
          onClick={card.onClick && card.onClick}
        >
          <Logo />
          <Typography variant="bodyXs" className="text-[#1d252f] mt-1 whitespace-nowrap">
            {card.text}
          </Typography>
        </Link>
      )}
    </div>
  )
})
RecommendationCard.displayName = 'RecommendationCard'

const debtResolutionCard = {
  text: 'Debt Resolution',
  icon: DebtResolutionLogo,
  link: '/get-started?brand=achieve&section=achieve_dr&utm_medium=organic&utm_media=organic',
}

const personalLoansCard = {
  text: 'Personal Loans',
  icon: PersonalLogo,
  link: '/get-started?brand=achieve&section=achieve_pl&utm_medium=organic&utm_media=organic',
}

const homeLoansCard = {
  text: 'Home Loan',
  icon: HomeLogo,
  link: '/get-started?brand=achieve&section=achieve_hl&utm_medium=organic&utm_media=organic',
}

const goodCard = {
  text: 'GOOD App',
  icon: GoodLogo,
}

const moloCard = {
  text: 'MoLO App',
  icon: MoloLogo,
}

const dtiToRecomendationMap = {
  [DTI_RATIO_ASSESSMENT_MAP.low]: {
    eyebrow: 'Our Recommendations',
    cards: [moloCard, goodCard],
    text: 'Your DTI ratio is 36% or less! We recommend our money saving mobile apps to maintain your low ratio so you continue to be financially healthy.',
    linkText: 'Explore more options',
    linkHref: '/get-started?brand=achieve&section=achieve_web&utm_medium=organic&utm_media=organic',
  },
  [DTI_RATIO_ASSESSMENT_MAP.medium]: {
    eyebrow: 'Our Recommendations',
    cards: [debtResolutionCard, personalLoansCard, homeLoansCard],
    text: 'Your DTI ratio is nearing 43%—the highest ratio typical lenders allow for most loans. We recommend any of our personalized solutions to improve your financial health.',
    linkText: 'Explore your options',
    linkHref: '/get-started?brand=achieve&section=achieve_web&utm_medium=organic&utm_media=organic',
  },
  [DTI_RATIO_ASSESSMENT_MAP.high]: {
    eyebrow: 'Our Recommendations',
    cards: [debtResolutionCard],
    text: 'Since your DTI ratio is high, we recommend Debt Resolution to help lower it. It’s currently over 43%—the highest ratio typical lenders allow for most loans.',
    linkText: 'Get your debt plan',
    linkHref: '/get-started?brand=achieve&section=achieve_dr&utm_medium=organic&utm_media=organic',
  },
  [DTI_RATIO_ASSESSMENT_MAP.maximum]: {
    eyebrow: 'Our Recommendations',
    cards: [debtResolutionCard],
    text: 'Since your DTI ratio is high, we recommend Debt Resolution to help lower it. It’s currently over 43%—the highest ratio typical lenders allow for most loans.',
    linkText: 'Get your debt plan',
    linkHref: '/get-started?brand=achieve&section=achieve_dr&utm_medium=organic&utm_media=organic',
  },
}

export const DtiRecommendation = ({
  dtiRatioAssessment = DTI_RATIO_ASSESSMENT_MAP.low,
  credit = 0,
  debtResolutionCardLink = debtResolutionCard.link,
  personalLoansCardLink = personalLoansCard.link,
  homeLoansCardLink = homeLoansCard.link,
  goodAppStoreLink,
  goodGooglePlayLink,
  moloAppStoreLink,
  moloGooglePlayLink,
  lowRatioLinkHref = dtiToRecomendationMap.low.linkHref,
  mediumRatioLinkHref = dtiToRecomendationMap.medium.linkHref,
  highRatioLinkHref = dtiToRecomendationMap.high.linkHref,
  onDebtResolutionClick,
  onPersonalLoansClick,
  onHomeLoansClick,
  onGoodAppClick,
  onMoloAppClick,
  onLowRatioLinkClick,
  onMediumRatioLinkClick,
  onHighRatioLinkClick,
  amountParam = 'loan_amount',
}) => {
  const [cardState, setCardState] = useState([])
  const [device, setDevice] = useState(null)

  // The user agent is only available on the client
  useEffect(() => {
    setDevice(actualDevice())
  }, [])

  const [linkHrefWithCredit, setLinkHrefWithCredit] = useState('')
  const [dtiRatioLinkOnClickHandler, setDtiRatioLinkOnClickHandler] = useState()

  useEffect(() => {
    const cards = dtiToRecomendationMap[dtiRatioAssessment].cards
    let onClickHandler
    // Map over the cards array to update the links based on the provided props or defaults
    // and also set the onClickHandler if provided
    const updatedCards = cards.map((card) => {
      let cardLink
      let stores
      switch (card.text) {
        case 'Debt Resolution':
          cardLink = debtResolutionCardLink
          onClickHandler = onDebtResolutionClick
          break
        case 'Personal Loans':
          cardLink = personalLoansCardLink
          onClickHandler = onPersonalLoansClick
          break
        case 'Home Loan':
          cardLink = homeLoansCardLink
          onClickHandler = onHomeLoansClick
          break
        case 'GOOD App':
          stores = {
            appStore: goodAppStoreLink || GOOD_APP_LINKS.appStore,
            googlePlay: goodGooglePlayLink || GOOD_APP_LINKS.googlePlay,
          }
          onClickHandler = onGoodAppClick
          break
        case 'MoLO App':
          stores = {
            appStore: moloAppStoreLink || MOLO_APP_LINKS.appStore,
            googlePlay: moloGooglePlayLink || MOLO_APP_LINKS.googlePlay,
          }
          onClickHandler = onMoloAppClick
          break
        default:
          cardLink = card.link
      }

      // Update each card's link with the loan_amount query parameter, except for MoLO and GOOD apps
      if (stores) {
        return {
          ...card,
          stores,
          device,
          onClick: onClickHandler,
        }
      }
      return {
        ...card,
        link: `${cardLink}&${amountParam}=${credit}`,
        onClick: onClickHandler,
      }
    })

    setCardState(withUuid(updatedCards))

    // Update the linkHref based on the dtiRatioAssessment prop or default
    let linkHref
    let dtiRatioLinkOnClickHandler
    switch (dtiRatioAssessment) {
      case 'low':
        linkHref = lowRatioLinkHref
        dtiRatioLinkOnClickHandler = onLowRatioLinkClick
        break
      case 'medium':
        linkHref = mediumRatioLinkHref
        dtiRatioLinkOnClickHandler = onMediumRatioLinkClick
        break
      case 'high':
        linkHref = highRatioLinkHref
        dtiRatioLinkOnClickHandler = onHighRatioLinkClick
        break
      case 'maximum':
        linkHref = highRatioLinkHref
        dtiRatioLinkOnClickHandler = onHighRatioLinkClick
        break
    }

    const updatedLinkHref = `${linkHref}&${amountParam}=${credit}`
    setLinkHrefWithCredit(updatedLinkHref)
    setDtiRatioLinkOnClickHandler(() => dtiRatioLinkOnClickHandler)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dtiRatioAssessment,
    credit,
    debtResolutionCardLink,
    personalLoansCardLink,
    homeLoansCardLink,
    goodAppStoreLink,
    goodGooglePlayLink,
    moloAppStoreLink,
    moloGooglePlayLink,
    device,
    lowRatioLinkHref,
    mediumRatioLinkHref,
    highRatioLinkHref,
  ])

  return (
    <div className="mt-2 flex flex-col justify-center items-center">
      <div className="mb-4">
        <Typography
          variant="labelMd"
          fontWeight="bold"
          className="px-4 py-2 rounded bg-[#f5f9ff] text-[#154199] whitespace-nowrap"
        >
          {dtiToRecomendationMap[dtiRatioAssessment].eyebrow.toUpperCase()}
        </Typography>
      </div>
      <div className="mb-4 flex justify-center items-center text-center">
        <Typography variant="bodySm"> {dtiToRecomendationMap[dtiRatioAssessment].text}</Typography>
      </div>
      <div
        className="flex justify-center items-start mb-2 recommendation-cards-section [&>div]:relative"
        data-testid="dti-recommendation-cards"
      >
        {cardState &&
          cardState.map((e, index) => (
            <RecommendationCard key={e?.uuid} card={e} isLast={index + 1 === cardState.length} />
          ))}
      </div>
      <Link
        href={linkHrefWithCredit}
        className="mb-4"
        onClick={() => {
          if (dtiRatioLinkOnClickHandler) {
            dtiRatioLinkOnClickHandler()
          }
        }}
      >
        <Typography variant="bodySm">
          {dtiToRecomendationMap[dtiRatioAssessment].linkText}
        </Typography>
      </Link>
    </div>
  )
}
